/* React */
import React, { useMemo, useRef, useEffect } from "react";

/* d3.js */
import * as d3 from "d3";

/* Hooks */
import { useEffectiveShowNoValueEntries } from "src/utils/hooks/useEffectiveShowNoValueEntries";

import { createVerticaBoxplot } from "src/components/charts/vertical-boxplot/verticalBoxplotRenderer";

/* Components */
import ChartTooltip from "src/components/shared/chart-tooltip/ChartTooltip";

/* Constants */
import {
  excludeCohortNoValueGroups,
  orderCohortGroups,
} from "src/shared/constants/charts/NoValueStates";

/* Interfaces */
import { BoxplotProps } from "src/shared/interfaces/charts/Boxplot";
import { ChartDimensionsProps } from "src/shared/interfaces/charts/BaseChart";
import { ChartTooltipRef } from "src/shared/interfaces/ChartTooltip";

const VerticalBoxplot: React.FC<{
  data: BoxplotProps[];
  dimensions: ChartDimensionsProps;
}> = ({ data, dimensions }) => {
  const svgRef = useRef<SVGSVGElement | null>(null);
  const tooltipRef = useRef<ChartTooltipRef>(null);
  const height: number = 421;

  // Gated by custom-cohort state so the boxplot never renders no-value groups
  // the request (and the page/legend) treat as off - see the hook's docs.
  const showNoValueEntries = useEffectiveShowNoValueEntries();

  const chartData = useMemo(
    () =>
      orderCohortGroups(
        excludeCohortNoValueGroups(data, showNoValueEntries)
      ),
    [data, showNoValueEntries]
  );

  useEffect(() => {
    if (!svgRef.current) return;

    const svg = d3.select(svgRef.current);
    svg.selectAll("*").remove();

    // Filtering out no-value groups can leave no series at all; the d3 scale
    // math below assumes at least one, so bail out on an empty chart.
    if (chartData.length === 0) return;

    createVerticaBoxplot(svg, tooltipRef, chartData, dimensions);
  }, [chartData, dimensions]);

  return (
    <>
      <ChartTooltip ref={tooltipRef} />
      <svg
        ref={svgRef}
        height={height}
        width={dimensions.width}
      />
    </>
  );
};

export default VerticalBoxplot;
